import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import Button from '@renderer/components/Button'
import { useTranslation } from 'react-i18next'
import { FaTimes, FaPrint, FaChevronDown, FaChevronRight, FaCheckCircle, FaClock, FaCloudUploadAlt, FaQuestionCircle, FaExclamationTriangle, FaSync, FaTrash } from 'react-icons/fa'
import { FaSpinner } from 'react-icons/fa6'
import { PrintUtils, buildPrintOptions } from '@renderer/utils/printUtils'
import { usePrintStore } from '@renderer/stores/printStore'
import cogoToast from '@successtar/cogo-toast'
import { logPrintEvent } from '@renderer/utils/printEventLogger'
import { useHubStore } from '@renderer/stores/hubStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'

interface UploadStatus {
  isFullyUploaded: boolean
  uploadProgress: number
  // false ⇒ session has no tracked media rows (filesystem-only); must not read as uploaded.
  hasMetadata?: boolean
  // false ⇒ status could not be determined (DB unavailable); must not read as uploaded.
  statusKnown?: boolean
}

// A session only counts as "uploaded" when the status is known, it has tracked
// media rows, and every one of them is uploaded. Anything else (unknown / untracked
// / partially pending) must never render as a green "uploaded" check. The `!== false`
// checks keep older main builds (which omit the new fields) behaving as before.
const isConfirmedUploaded = (status?: UploadStatus | null): boolean =>
  Boolean(
    status &&
      status.statusKnown !== false &&
      status.hasMetadata !== false &&
      status.isFullyUploaded
  )

interface SessionData {
  sessionId: string
  createdAt: number
  /** Owning project, when the local DB row is known (absent for FS-only sessions). */
  projectId?: string
}
interface StripPhoto {
  sessionId: string
  name: string
  path: string
  base64: string | null
  createdAt: number
  frameId?: string | null
}

interface DayGroup {
  dateKey: string
  dateLabel: string
  sessions: SessionData[]
}

interface ReprintModalProps {
  isOpen: boolean
  onClose: () => void
}

// v2.1.0-2.1.2 have listSessions + getSessionPhotos only; getMediaUploadStatus and retryUpload were added later.
// When missing, we hide status/retry UI to avoid errors and show a neutral experience.
const hasReprintStatusSupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { getMediaUploadStatus?: unknown } } }).api?.reprint?.getMediaUploadStatus === 'function'
const hasReprintRetrySupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { retryUpload?: unknown } } }).api?.reprint?.retryUpload === 'function'
// Re-sync of the SESSION RECORD (independent of media). Added later than retryUpload.
const hasReprintResyncSupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { resyncSession?: unknown } } }).api?.reprint?.resyncSession === 'function'
// Local delete of a session's media. Added later than resyncSession.
const hasReprintDeleteSupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as { api?: { reprint?: { deleteSession?: unknown } } }).api?.reprint?.deleteSession === 'function'

const ReprintModal: React.FC<ReprintModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState<SessionData[]>([])
  // Map of sessionId -> StripPhoto[]
  const [stripsCache, setStripsCache] = useState<Map<string, StripPhoto[]>>(new Map())
  // Set of sessionIds that have been loaded (even if empty strips)
  const [loadedSessionIds, setLoadedSessionIds] = useState<Set<string>>(new Set())
  const [loadingDays, setLoadingDays] = useState<Set<string>>(new Set())

  const [selectedStrip, setSelectedStrip] = useState<StripPhoto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set())
  const [uploadStatuses, setUploadStatuses] = useState<Map<string, UploadStatus>>(new Map())
  const [retryingSession, setRetryingSession] = useState<string | null>(null)
  // dateKey of the day whose "Retry All" is in flight; one at a time.
  const [retryingAllDay, setRetryingAllDay] = useState<string | null>(null)
  // true ⇒ there is no booth to ask (browser build): no local sessions, no retry,
  // no re-sync. Distinct from "the booth answered and has nothing".
  const [reprintUnavailable, setReprintUnavailable] = useState(false)
  // Session awaiting delete confirmation, and the one whose delete is in flight.
  const [pendingDeleteSessionId, setPendingDeleteSessionId] = useState<string | null>(null)
  const [deletingSession, setDeletingSession] = useState<string | null>(null)

  const showStatusAndRetry = hasReprintStatusSupport() && hasReprintRetrySupport()
  // Independent of media status — a session's media can be fully uploaded while its
  // record is still missing from Transaksi, which is exactly what this recovers.
  const showResync = hasReprintResyncSupport()
  const showDelete = hasReprintDeleteSupport()

  // Load sessions on open
  useEffect(() => {
    if (isOpen) {
      loadSessions()
    } else {
      // Reset state when closed
      setSessions([])
      setStripsCache(new Map())
      setLoadedSessionIds(new Set())
      setSelectedStrip(null)
      setCollapsedDays(new Set())
      setLoadingDays(new Set())
      setReprintUnavailable(false)
      setPendingDeleteSessionId(null)
    }
  }, [isOpen])



  const loadSessions = async (): Promise<void> => {
    setIsLoading(true)
    try {
      if (
        typeof window === 'undefined' ||
        !window.api ||
        // @ts-ignore - reprint API is added in preload but not yet in types
        !window.api.reprint ||
        // @ts-ignore - reprint API checks
        typeof window.api.reprint.listSessions !== 'function'
      ) {
        console.warn('Reprint API not available - running in web environment')
        setReprintUnavailable(true)
        setIsLoading(false)
        return
      }
      setReprintUnavailable(false)

      const sessionsResult = await window.api.reprint.listSessions()
      if (!sessionsResult.success || !sessionsResult.sessions) {
        console.error('Failed to load sessions:', sessionsResult.error)
        return
      }

      // Multi-project hub: a PC running a hub prints koran and strips from
      // different projects, and `getPrinterForFrame` resolves against the
      // ACTIVE project's catalogue — a koran session listed while the strip
      // member is on screen has no matching frame and would reprint on the
      // wrong printer. Scope the list to the member on screen; sessions with
      // no recorded project (filesystem-only) stay visible. Standalone booths
      // are untouched: the filter only engages while a hub context exists.
      const hub = useHubStore.getState()
      const activeProjectId = useSessionStateStore.getState().projectId
      const scopeToProject =
        Boolean(hub.hubId) && hub.memberProjectId === activeProjectId && activeProjectId
      const allSessions: SessionData[] = scopeToProject
        ? sessionsResult.sessions.filter(
            (s: SessionData) => !s.projectId || s.projectId === activeProjectId
          )
        : sessionsResult.sessions
      setSessions(allSessions)

      // Fetch upload status only when API exists (v2.1.5+; v2.1.0-2.1.2 lack getMediaUploadStatus)
      const uniqueSessionIds = allSessions.map((s) => s.sessionId)
      const statusMap = new Map<string, UploadStatus>()
      if (hasReprintStatusSupport()) {
        await Promise.all(
          uniqueSessionIds.map(async (sid) => {
            try {
              // @ts-ignore - reprint API exists when hasReprintStatusSupport()
              const result = await window.api.reprint.getMediaUploadStatus(sid)
              if (result.success && result.status) {
                statusMap.set(sid, {
                  isFullyUploaded: result.status.isFullyUploaded,
                  uploadProgress: result.status.uploadProgress,
                  hasMetadata: result.status.hasMetadata,
                  statusKnown: result.status.statusKnown
                })
              }
            } catch {
              // Silently skip failed status checks
            }
          })
        )
      }
      setUploadStatuses(statusMap)
    } catch (error) {
      console.error('Failed to load sessions:', error)
      cogoToast.error(t('reprint.errorLoadingSessions', 'Failed to load sessions'))
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStripsForSessions = async (sessionsToLoad: SessionData[]): Promise<void> => {
    if (sessionsToLoad.length === 0) return

    try {
       // Filter out sessions that are already loaded
      const neededSessions = sessionsToLoad.filter(s => !loadedSessionIds.has(s.sessionId))
      if (neededSessions.length === 0) return

      const newStripsMap = new Map(stripsCache)
      const newLoadedIds = new Set(loadedSessionIds)

      const getSessionPhotos =
        typeof window.api?.reprint?.getSessionPhotos === 'function'
          ? window.api.reprint.getSessionPhotos
          : null

      await Promise.all(
        neededSessions.map(async (session) => {
          try {
            if (!getSessionPhotos) {
              newLoadedIds.add(session.sessionId)
              return
            }
            // @ts-ignore - reprint API
            const photosResult = await getSessionPhotos(session.sessionId)
            if (photosResult.success && photosResult.photos) {
              // Filter for strip photos only (filename contains "-strip")
              const stripPhotos = photosResult.photos.filter((photo) =>
                photo.name.toLowerCase().includes('-strip')
              )
              const frameId = photosResult.frameId || null

              const strips = stripPhotos.map((photo) => ({
                sessionId: session.sessionId,
                name: photo.name,
                path: photo.path,
                base64: photo.base64,
                createdAt: session.createdAt * 1000,
                frameId
              }))

              newStripsMap.set(session.sessionId, strips)
            } else {
               newStripsMap.set(session.sessionId, [])
            }
            newLoadedIds.add(session.sessionId)
          } catch (e) {
            console.error(`Failed to load photos for session ${session.sessionId}`, e)
            newLoadedIds.add(session.sessionId) // Mark as loaded even if failed so we don't retry endlessly
          }
        })
      )

      setStripsCache(newStripsMap)
      setLoadedSessionIds(newLoadedIds)
    } catch (error) {
      console.error('Error fetching strips:', error)
    }
  }

  // Group sessions by day
  const groupedSessionsByDay = useMemo((): DayGroup[] => {
    const groups = new Map<string, SessionData[]>()

    // Sort sessions by createdAt descending first
    const sortedSessions = [...sessions].sort((a, b) => b.createdAt - a.createdAt)

    for (const session of sortedSessions) {
      const date = new Date(session.createdAt * 1000)
      const dateKey = date.toISOString().split('T')[0] // YYYY-MM-DD

      if (!groups.has(dateKey)) {
        groups.set(dateKey, [])
      }
      groups.get(dateKey)!.push(session)
    }

    const result: DayGroup[] = []
    // Groups keys are already sorted if we iterate insertion order? No, set/map iteration is insertion order.
    // But let's sort keys explicitly to be safe
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a))

    for (const dateKey of sortedKeys) {
      const date = new Date(dateKey)
      const dateLabel = date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      result.push({
        dateKey,
        dateLabel,
        sessions: groups.get(dateKey)!
      })
    }

    return result
  }, [sessions])

  // Default day groups to collapsed the first time they appear. Days already on
  // screen keep their state, so deleting a session doesn't fold the open day shut.
  const seenDayKeys = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!isOpen) {
      seenDayKeys.current = new Set()
      return
    }
    const newKeys = groupedSessionsByDay
      .map((group) => group.dateKey)
      .filter((key) => !seenDayKeys.current.has(key))
    if (newKeys.length === 0) return
    newKeys.forEach((key) => seenDayKeys.current.add(key))
    setCollapsedDays((prev) => new Set([...prev, ...newKeys]))
  }, [groupedSessionsByDay, isOpen])

  const handleReprintRequest = useCallback(async (): Promise<void> => {
    if (!selectedStrip?.base64) return

    setIsPrinting(true)
    let printerName: string | undefined
    try {
      const { getPrinterForFrame } = usePrintStore.getState()

      // Get frame-specific printer configuration
      const frameId = selectedStrip.frameId || ''
      const { printerName: framePrinterName, settings } = getPrinterForFrame(frameId)
      printerName = framePrinterName

      // Use frame-specific settings, fallback to defaults if frameId is empty
      // Same option set a live session sends — see buildPrintOptions. This used
      // to be hand-assembled here and drifted: it omitted printBackground and
      // used `scaleFactor || 1`, so a settings object without an explicit scale
      // reprinted at 1% instead of 100%.
      const result = await PrintUtils.sendToPrinter(
        selectedStrip.base64,
        buildPrintOptions(printerName, settings, { copies: 1 })
      )

      if (result.success) {
        cogoToast.success(t('reprint.printSuccess', 'Print job sent successfully'))
        logPrintEvent({
          eventType: 'operator_reprint',
          copies: 1,
          printerName,
          printerType: result.printerType,
          success: true
        })
      } else {
        const errorMsg = result.error || result.failureReason || 'Unknown error'
        void logPrintEvent({
          eventType: 'operator_reprint',
          copies: 1,
          printerName,
          printerType: result.printerType,
          success: false,
          error: errorMsg
        })
        cogoToast.error(result.error || t('reprint.printError', 'Failed to print'))
      }
    } catch (error) {
      console.error('Failed to print:', error)
      const errMsg = error instanceof Error ? error.message : 'Unknown error'
      void logPrintEvent({ eventType: 'operator_reprint', copies: 1, printerName, success: false, error: errMsg })
      cogoToast.error(t('reprint.printError', 'Failed to print'))
    } finally {
      setIsPrinting(false)
    }
  }, [selectedStrip, t])

  const handleRetryUpload = useCallback(async (sessionId: string, e?: React.MouseEvent): Promise<void> => {
    if (e) {
      e.stopPropagation()
    }
    if (typeof window.api?.reprint?.retryUpload !== 'function') {
      cogoToast.error(t('reprint.retryError', 'Failed to retry upload'))
      return
    }
    setRetryingSession(sessionId)
    try {
      // @ts-ignore - reprint API
      const result = await window.api.reprint.retryUpload(sessionId)
      if (result.success) {
        const queued = result.queued ?? 0
        const verified = result.verifiedUploaded ?? 0
        const corrected = result.correctedFalseUploaded ?? 0
        const missing = result.missingLocal ?? 0
        if (result.inconclusive) {
          // Offline: we queued real upload jobs but couldn't verify server state.
          cogoToast.info(
            t('reprint.retryQueuedOffline', `Queued ${queued} file(s) — will upload when back online`, { count: queued })
          )
        } else if (corrected > 0) {
          // Files that showed as uploaded but were never actually on the server —
          // reset and re-queued so they upload for real this time.
          cogoToast.warn(
            t(
              'reprint.retryCorrected',
              `${corrected} file(s) weren't actually uploaded — re-queued`,
              { count: corrected }
            )
          )
        } else if (queued > 0) {
          cogoToast.success(
            t('reprint.retrySuccess', `Queued ${queued} file(s) for upload`, { count: queued })
          )
        } else if (verified > 0) {
          // The files were actually already on the server; we just corrected the status.
          cogoToast.success(
            t('reprint.retryVerified', `Confirmed ${verified} file(s) already uploaded`, { count: verified })
          )
        } else if (missing > 0) {
          // Nothing on the server and no local copy — can't upload; status is honestly "failed".
          cogoToast.error(
            t('reprint.retryMissing', `${missing} file(s) are missing locally and were never uploaded`, { count: missing })
          )
        } else {
          cogoToast.info(t('reprint.retryNoFiles', 'Nothing to upload for this session'))
        }
      } else if (result.inconclusive) {
        // We could not reach the server to verify — do not imply anything uploaded.
        cogoToast.error(
          result.error ||
            t('reprint.retryInconclusive', 'Could not verify uploads — check the internet connection and try again')
        )
      } else {
        cogoToast.error(result.error || t('reprint.retryError', 'Failed to retry upload'))
      }

      // Refresh upload status for this session
      // @ts-ignore - reprint API
      if (window.api?.reprint?.getMediaUploadStatus) {
        try {
          // @ts-ignore - reprint API
          const statusResult = await window.api.reprint.getMediaUploadStatus(sessionId)
          if (statusResult.success && statusResult.status) {
            const s = statusResult.status
            setUploadStatuses((prev) => {
              const newMap = new Map(prev)
              newMap.set(sessionId, {
                isFullyUploaded: s.isFullyUploaded,
                uploadProgress: s.uploadProgress,
                hasMetadata: s.hasMetadata,
                statusKnown: s.statusKnown
              })
              return newMap
            })
          }
        } catch {
          // Silently skip
        }
      }
    } catch (error) {
      console.error('Failed to retry upload:', error)
      cogoToast.error(t('reprint.retryError', 'Failed to retry upload'))
    } finally {
      setRetryingSession(null)
    }
  }, [t])

  // "Retry All" for one day — sequential, one day at a time, on purpose. Every
  // retry first asks the server to verify that session's media (an anonymous
  // GET /api/gallery?sessionId) before re-queueing, so firing a whole day at
  // once made one click cost 70+ concurrent server requests — and the button
  // stayed clickable while they hung, so the operator clicked again. That fan-out
  // (72 sessions × ~9 clicks inside 10 s) was the 2026-08-22 /api/gallery outage
  // (464 × MongoWaitQueueTimeoutError). Per-session toasts are unchanged.
  const handleRetryAllForDay = useCallback(
    async (dateKey: string, sessions: SessionData[], e: React.MouseEvent): Promise<void> => {
      e.stopPropagation()
      if (retryingAllDay !== null) return
      const notUploaded = sessions.filter(
        (s) => !isConfirmedUploaded(uploadStatuses.get(s.sessionId))
      )
      if (notUploaded.length === 0) return
      setRetryingAllDay(dateKey)
      try {
        for (const s of notUploaded) {
          await handleRetryUpload(s.sessionId)
        }
      } finally {
        setRetryingAllDay(null)
      }
    },
    [retryingAllDay, uploadStatuses, handleRetryUpload]
  )

  // Re-push SESSION RECORDS (not media) for a day's sessions so any that uploaded
  // to the gallery but never landed in the operator "Transaksi" list reappear.
  // Server-side upsert is idempotent, so re-syncing already-synced sessions is a
  // safe no-op. One summary toast instead of one-per-session.
  const handleResyncDay = useCallback(
    async (sessionsToSync: SessionData[], e?: React.MouseEvent): Promise<void> => {
      if (e) e.stopPropagation()
      const resync = (window as unknown as {
        api?: { reprint?: { resyncSession?: (id: string) => Promise<{ success: boolean; notFound?: boolean; error?: string }> } }
      }).api?.reprint?.resyncSession
      if (typeof resync !== 'function') {
        cogoToast.error(t('reprint.resyncUnsupported', 'Re-sync is not supported in this app version'))
        return
      }
      let ok = 0
      let notFound = 0
      let failed = 0
      for (const s of sessionsToSync) {
        try {
          const r = await resync(s.sessionId)
          if (r.success) ok++
          else if (r.notFound) notFound++
          else failed++
        } catch {
          failed++
        }
      }
      if (ok > 0) {
        cogoToast.success(
          t('reprint.resyncDone', `Re-synced ${ok} session record(s) — they will appear in Transaksi shortly`, { count: ok })
        )
      }
      if (notFound > 0) {
        cogoToast.info(
          t('reprint.resyncNotFound', `${notFound} session(s) had no local record to re-sync`, { count: notFound })
        )
      }
      if (failed > 0) {
        cogoToast.error(
          t('reprint.resyncFailed', `${failed} session(s) failed to re-sync`, { count: failed })
        )
      }
    },
    [t]
  )

  // Deletes the session's local media from this booth (all of its strips, not
  // just the one clicked). Server copies are untouched.
  const handleDeleteSession = useCallback(
    async (sessionId: string): Promise<void> => {
      if (typeof window.api?.reprint?.deleteSession !== 'function') {
        cogoToast.error(t('reprint.deleteUnsupported', 'Delete is not supported in this app version'))
        return
      }
      setDeletingSession(sessionId)
      try {
        const result = await window.api.reprint.deleteSession(sessionId)
        if (result.success) {
          setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId))
          setStripsCache((prev) => {
            const next = new Map(prev)
            next.delete(sessionId)
            return next
          })
          setLoadedSessionIds((prev) => {
            const next = new Set(prev)
            next.delete(sessionId)
            return next
          })
          setUploadStatuses((prev) => {
            const next = new Map(prev)
            next.delete(sessionId)
            return next
          })
          setSelectedStrip((prev) => (prev?.sessionId === sessionId ? null : prev))
          cogoToast.success(t('reprint.deleteSuccess', 'Session deleted from this booth'))
        } else if (result.busy) {
          cogoToast.warn(
            t('reprint.deleteBusy', 'This session is uploading right now. Try again in a moment.')
          )
        } else {
          cogoToast.error(result.error || t('reprint.deleteError', 'Failed to delete session'))
        }
      } catch (error) {
        console.error('Failed to delete session:', error)
        cogoToast.error(t('reprint.deleteError', 'Failed to delete session'))
      } finally {
        setDeletingSession(null)
        setPendingDeleteSessionId(null)
      }
    },
    [t]
  )

  const toggleDayCollapse = async (dateKey: string): Promise<void> => {
    const isExpanding = collapsedDays.has(dateKey)
    
    setCollapsedDays((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey)
      } else {
        newSet.add(dateKey)
      }
      return newSet
    })

    if (isExpanding) {
      // Find the group and load strips for it
      const group = groupedSessionsByDay.find(g => g.dateKey === dateKey)
      if (group && group.sessions.length > 0) {
        // Check if we need to load any sessions
        const sessionsToLoad = group.sessions.filter(s => !loadedSessionIds.has(s.sessionId))
        
        if (sessionsToLoad.length > 0) {
          setLoadingDays(prev => new Set(prev).add(dateKey))
          await fetchStripsForSessions(sessionsToLoad)
          setLoadingDays(prev => {
            const newSet = new Set(prev)
            newSet.delete(dateKey)
            return newSet
          })
        }
      }
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
        <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-carbon">
              {selectedStrip
                ? t('reprint.preview', 'Preview')
                : t('reprint.title', 'Reprint Strip Photos')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors p-1"
              title={t('common.close', 'Close')}
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : selectedStrip ? (
              /* Strip Preview */
              <div className="flex flex-col items-center gap-4">
                <img
                  src={selectedStrip.base64 || ''}
                  alt={selectedStrip.name}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                />
                <div className="text-center">
                  <p className="font-medium text-carbon">
                    {t('reprint.session', 'Session')}: {selectedStrip.sessionId}
                  </p>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedStrip.createdAt).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ) : reprintUnavailable ? (
              /* Browser build: reprint, retry and re-sync all live in the desktop app.
                 Saying "No sessions found" here reads as "your photos are gone" and
                 sends operators hunting for a Retry All button that cannot exist.
                 Most operators who see this run the booth itself in a browser, which
                 keeps no local copy (uploadFile skips saveFileToDB without preload),
                 so the copy must not promise that the photos are safe somewhere. */
              <div className="max-w-xl mx-auto py-8 text-center">
                <FaExclamationTriangle className="mx-auto text-3xl text-yellow-500 mb-3" />
                <p className="font-semibold text-carbon mb-2">
                  {t('reprint.unavailableTitle', "Reprint isn't available in the browser")}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  {t(
                    'reprint.unavailableBody',
                    'The session list, reprint and re-upload only exist in the Dreambooth desktop app, because that app keeps a copy of the photos on the booth PC.'
                  )}
                </p>
                <p className="text-sm text-gray-600 text-left inline-block">
                  {t(
                    'reprint.unavailableHowTo',
                    'If this booth runs in a browser, photos that failed to upload cannot be sent again from here. Photos that did upload are in the session gallery on the dashboard. If your booth uses the desktop app, open Reprint there: Retry All and Re-sync records are on that screen.'
                  )}
                </p>
              </div>
            ) : groupedSessionsByDay.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                {t('reprint.noSessions', 'No sessions found')}
              </p>
            ) : (
              /* Collapsible sections by day */
              <div className="space-y-4">
                {groupedSessionsByDay.map((group) => {
                  const isLoadingDay = loadingDays.has(group.dateKey)
                  const isCollapsed = collapsedDays.has(group.dateKey)
                  
                  // Calculate stats (only when status API is available)
                  const sessionCount = group.sessions.length
                  const uploadedCount = showStatusAndRetry
                    ? group.sessions.filter((s) => isConfirmedUploaded(uploadStatuses.get(s.sessionId))).length
                    : 0
                  const allUploaded = showStatusAndRetry ? uploadedCount === sessionCount : true

                  // Collect all loaded strips for this group
                  const groupStrips: StripPhoto[] = []
                  group.sessions.forEach(s => {
                    const sessionStrips = stripsCache.get(s.sessionId)
                    if (sessionStrips) {
                      groupStrips.push(...sessionStrips)
                    }
                  })

                  // Sort strips by time (createdAt) descending
                  groupStrips.sort((a, b) => b.createdAt - a.createdAt)

                  // Helper: upload status badge (only when status/retry API available; v2.1.0-2.1.2 lack it)
                  const renderUploadBadge = (): JSX.Element | null => {
                    if (!showStatusAndRetry) return null
                    return (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        allUploaded ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {uploadedCount}/{sessionCount} {t('reprint.uploaded', 'uploaded')}
                      </span>
                    )
                  }

                  return (
                  <div key={group.dateKey} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Day header - clickable to collapse */}
                    <button
                      type="button"
                      onClick={() => toggleDayCollapse(group.dateKey)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isCollapsed ? (
                          <FaChevronRight className="text-gray-500" />
                        ) : (
                          <FaChevronDown className="text-gray-500" />
                        )}
                        <span className="font-semibold text-carbon">{group.dateLabel}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {renderUploadBadge()}
                        {showStatusAndRetry && !allUploaded && (
                          <button
                            type="button"
                            onClick={(e) => handleRetryAllForDay(group.dateKey, group.sessions, e)}
                            disabled={retryingAllDay !== null}
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('reprint.retryUploadAll', 'Retry upload for all pending sessions')}
                          >
                            {retryingAllDay === group.dateKey ? (
                              <FaSpinner className="animate-spin text-xs" />
                            ) : (
                              <FaCloudUploadAlt className="text-xs" />
                            )}
                            {t('reprint.retryAll', 'Retry All')}
                          </button>
                        )}
                        {showResync && (
                          <button
                            type="button"
                            onClick={(e) => handleResyncDay(group.sessions, e)}
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors flex items-center gap-1"
                            title={t('reprint.resyncAllTitle', 'Re-sync session records to the dashboard Transaksi list (fixes sessions missing from history)')}
                          >
                            <FaSync className="text-xs" />
                            {t('reprint.resyncAll', 'Re-sync records')}
                          </button>
                        )}
                        <span className="text-sm text-gray-500">
                           {/* Show session count initially, or strip count if loaded? 
                               User expects "trips" count maybe. 
                               But we don't know strip count until loaded. 
                               Let's show "X session(s)" to be accurate.
                           */}
                           {sessionCount} {t('reprint.sessionCount', 'session(s)')}
                        </span>
                      </div>
                    </button>

                    {/* Strips grid - collapsible */}
                    {!isCollapsed && (
                      <div className="p-4">
                         {isLoadingDay ? (
                            <div className="flex justify-center py-8">
                                <FaSpinner className="animate-spin text-blue-500 text-2xl" />
                                <span className="ml-2 text-gray-500">{t('common.loading', 'Loading photos...')}</span>
                            </div>
                         ) : groupStrips.length === 0 ? (
                            <div className="text-center text-gray-400 text-sm py-4">
                                {t('reprint.noStripsInSession', 'No strip photos found in these sessions')}
                            </div>
                         ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {groupStrips.map((strip, index) => {
                                  // Upload status indicator (only when status API available)
                                  const renderUploadStatus = (): JSX.Element | null => {
                                    if (!showStatusAndRetry) return null
                                    const status = uploadStatuses.get(strip.sessionId)
                                    if (!status) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadPending', 'Upload pending')}>
                                        <FaClock className="text-gray-400 text-xs" />
                                      </span>
                                    )
                                    // Status could not be determined — never imply uploaded.
                                    if (status.statusKnown === false) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadUnknown', 'Upload status unknown')}>
                                        <FaQuestionCircle className="text-gray-400 text-xs" />
                                      </span>
                                    )
                                    // Confirmed uploaded (known + tracked + all files uploaded).
                                    if (isConfirmedUploaded(status)) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadComplete', 'Fully uploaded')}>
                                        <FaCheckCircle className="text-green-500 text-xs" />
                                      </span>
                                    )
                                    // Strip exists on disk but was never tracked/uploaded — not safe to call uploaded.
                                    if (status.hasMetadata === false) return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={t('reprint.uploadNotTracked', 'Not uploaded (no record found)')}>
                                        <FaExclamationTriangle className="text-yellow-500 text-xs" />
                                      </span>
                                    )
                                    return (
                                      <span className="absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-sm" title={`${t('reprint.uploading', 'Uploading')} ${status.uploadProgress}%`}>
                                        <FaSpinner className="animate-spin text-blue-500 text-xs" />
                                      </span>
                                    )
                                  }

                                  const isRetrying = retryingSession === strip.sessionId
                                  const sessionStatus = uploadStatuses.get(strip.sessionId)
                                  const showRetry = showStatusAndRetry && !isConfirmedUploaded(sessionStatus)

                                  return (
                                    <button
                                      key={`${strip.sessionId}-${strip.name}-${index}`}
                                      type="button"
                                      onClick={() => setSelectedStrip(strip)}
                                      className="relative bg-gray-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all shadow-sm hover:shadow-md p-2"
                                    >
                                      {strip.base64 ? (
                                        <img
                                          src={strip.base64}
                                          alt={strip.name}
                                          className="w-full h-auto rounded"
                                        />
                                      ) : (
                                        <div className="w-full h-32 flex items-center justify-center text-gray-400 text-xs">
                                          {t('reprint.loadError', 'Failed to load')}
                                        </div>
                                      )} 
                                      {/* Upload status indicator */}
                                      {renderUploadStatus()}
                                      {/* Retry upload button */}
                                      {showRetry && (
                                        <button
                                          type="button"
                                          onClick={(e) => handleRetryUpload(strip.sessionId, e)}
                                          disabled={isRetrying}
                                          className="absolute top-1 right-1 bg-white rounded-full p-1.5 shadow-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
                                          title={t('reprint.retryUpload', 'Retry upload')}
                                        >
                                          {isRetrying ? (
                                            <FaSpinner className="animate-spin text-blue-500 text-xs" />
                                          ) : (
                                            <FaCloudUploadAlt className="text-blue-500 text-xs" />
                                          )}
                                        </button>
                                      )}
                                      {/* Delete session button */}
                                      {showDelete && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setPendingDeleteSessionId(strip.sessionId)
                                          }}
                                          disabled={deletingSession !== null}
                                          className="absolute top-1 left-1 bg-white rounded-full p-1.5 shadow-sm hover:bg-red-50 transition-colors disabled:opacity-50"
                                          title={t('reprint.deleteSession', 'Delete session')}
                                        >
                                          {deletingSession === strip.sessionId ? (
                                            <FaSpinner className="animate-spin text-red-500 text-xs" />
                                          ) : (
                                            <FaTrash className="text-red-500 text-xs" />
                                          )}
                                        </button>
                                      )}
                                    </button>
                                  )
                                })}
                            </div>
                         )}
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div>
              {selectedStrip ? (
                <Button
                  text={t('common.back', 'Back')}
                  variant="secondary"
                  onClick={() => setSelectedStrip(null)}
                  className="!px-6 !py-2 !text-base"
                />
              ) : (
                <div className="text-sm text-gray-500">
                  {sessions.length > 0 && (
                    <span>
                      {sessions.length} {t('reprint.totalSessions', 'total session(s)')} • {groupedSessionsByDay.length}{' '}
                      {t('reprint.days', 'day(s)')}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                text={t('common.close', 'Close')}
                variant="secondary"
                onClick={onClose}
                className="!px-6 !py-2 !text-base"
              />
              {selectedStrip && showDelete && (
                <Button
                  text={
                    <span className="flex items-center gap-2">
                      <FaTrash />
                      {t('reprint.deleteButton', 'Delete')}
                    </span>
                  }
                  variant="secondary"
                  onClick={() => setPendingDeleteSessionId(selectedStrip.sessionId)}
                  className="!px-6 !py-2 !text-base !text-red-600 !border-red-600"
                  disabled={deletingSession !== null || isPrinting}
                />
              )}
              {selectedStrip && (
                <Button
                  text={
                    isPrinting
                      ? t('reprint.printing', 'Printing...')
                      : t('reprint.printButton', 'Print')
                  }
                  onClick={handleReprintRequest}
                  className="!px-6 !py-2 !text-base"
                  disabled={isPrinting || !selectedStrip.base64}
                >
                  <FaPrint className="mr-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {pendingDeleteSessionId && (() => {
        const stripCount = stripsCache.get(pendingDeleteSessionId)?.length ?? 0
        // Without status support we can't know — warn as if not uploaded.
        const notUploaded = !isConfirmedUploaded(uploadStatuses.get(pendingDeleteSessionId))
        const isDeleting = deletingSession === pendingDeleteSessionId
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110]">
            <div className="bg-white rounded-lg w-full max-w-md p-6 mx-4">
              <h3 className="text-lg font-bold text-carbon mb-2 flex items-center gap-2">
                <FaTrash className="text-red-500" />
                {t('reprint.deleteConfirmTitle', 'Delete this session?')}
              </h3>
              <p className="text-sm text-gray-600 mb-1">
                {t(
                  'reprint.deleteConfirmBody',
                  `All ${stripCount} strip photo(s) and the other media of this session will be removed from this booth PC. This cannot be undone.`,
                  { count: stripCount }
                )}
              </p>
              <p className="text-xs text-gray-400 mb-3 break-all">
                {t('reprint.session', 'Session')}: {pendingDeleteSessionId}
              </p>
              {notUploaded && (
                <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                  <FaExclamationTriangle className="text-yellow-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-yellow-800">
                    {t(
                      'reprint.deleteNotUploadedWarning',
                      'This session is not confirmed uploaded. Once deleted, its photos cannot be uploaded to the gallery anymore.'
                    )}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 mt-4">
                <Button
                  text={t('common.cancel', 'Cancel')}
                  variant="secondary"
                  onClick={() => setPendingDeleteSessionId(null)}
                  disabled={isDeleting}
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={
                    <span className="flex items-center gap-2">
                      {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                      {isDeleting
                        ? t('reprint.deleting', 'Deleting...')
                        : t('reprint.deleteButton', 'Delete')}
                    </span>
                  }
                  onClick={() => handleDeleteSession(pendingDeleteSessionId)}
                  disabled={isDeleting}
                  className="!px-6 !py-2 !text-base !bg-red-600 !text-white"
                />
              </div>
            </div>
          </div>
        )
      })()}
    </>
  )
}

export default ReprintModal
